/// <amd-module name="Wiki/PageRenamePanel/BrokenLinksTab" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent } from "VSS/Platform/Layout";
import { Spinner } from "VSSUI/Spinner";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { ILinkedArtifactDisplayData, OperationStatus } from "Wiki/PageRenamePanel/IPageRenamePanelSharedInterfaces";
import * as Resources from "Wiki/PageRenamePanel/Resources";

export interface BrokenLinkItem {
    link: string;
    updateState: OperationStatus;
}

export interface WikiBrokenLinkItem extends BrokenLinkItem {
    pagePath: string;
}

export interface WitBrokenLinkItem extends BrokenLinkItem {
    workItem: ILinkedArtifactDisplayData;
}

export interface IBrokenLinksTabProps {
    items: BrokenLinkItem[];
    artifactColumnHeaderName: string;
    getArtifactCellContent: (tableItem: BrokenLinkItem) => JSX.Element;
    showStatusColumn?: boolean;
    isLoading: boolean;
    loadingSpinnerText: string;
    onShowMoreLinkClick?(): void;
}

export class BrokenLinksTab extends VssComponent<IBrokenLinksTabProps> {
    private _artifact_column: ITableColumn<BrokenLinkItem>;
    private _link_column: ITableColumn<BrokenLinkItem>;
    private _status_column: ITableColumn<BrokenLinkItem>;

    constructor(props: IBrokenLinksTabProps) {
        super(props);

        this._artifact_column = {
            id: "artifact",
            name: this.props.artifactColumnHeaderName,
            renderCell: this._renderArtifactCell,
            readonly: true,
            minWidth: 100,
            width: new ObservableValue(-40),
            onSize: this._onSizeArtifactColumn
        };
        this._link_column = {
            id: "link",
            name: Resources.BrokenLinkComponentTable_ColumnHeader_Link,
            renderCell: this._renderLink,
            readonly: true,
            minWidth: 100,
            width: new ObservableValue(-60),
            onSize: this._onSizeLinkColumn
        };
        this._status_column = {
            id: "updateState",
            name: Resources.BrokenLinkComponentTable_ColumnHeader_Status,
            renderCell: this._renderStatus,
            readonly: true,
            width: 80,
            minWidth: 50,
            onSize: this._onSizeStatusColumn
        };
    }

    public render(): JSX.Element {
        let content: JSX.Element;
        if (this.props.isLoading) {
            content = <Spinner className={"brkn-link-spinner flex-self-start flex-grow"} label={this.props.loadingSpinnerText} />;
        } else if (this.props.items && this.props.items.length > 0) {
            content = (
                <Table
                    columns={this._getTableColumns(this.props.showStatusColumn)}
                    itemProvider={new ArrayItemProvider(this.props.items as any[])}
                    scrollable={true}
                />
            );
        } else {
            content = <label className={"brkn-lnk-zero-dta-msg"}>{Resources.BrokenLinkZeroDataMessage}</label>;
        }

        return <div className={"flex-row flex-grow v-scroll-auto"}>{content}</div>;
    }

    private _renderArtifactCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<BrokenLinkItem>,
        tableItem: BrokenLinkItem
    ): JSX.Element => {
        const content = this.props.getArtifactCellContent(tableItem);
        return this._renderCellContent(columnIndex, tableColumn, content);
    };

    private _renderLink = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<BrokenLinkItem>,
        tableItem: BrokenLinkItem
    ): JSX.Element => {
        const content =
            tableItem && tableItem.link ? (
                <Tooltip text={tableItem.link} overflowOnly>
                    <span className="text-ellipsis">{tableItem.link}</span>
                </Tooltip>
            ) : (
                undefined
            );
        return this._renderCellContent(columnIndex, tableColumn, content);
    };

    private _renderStatus = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<BrokenLinkItem>,
        tableItem: BrokenLinkItem
    ): JSX.Element => {
        let status: IStatusProps | undefined = undefined;
        if (tableItem && tableItem.updateState) {
            if (tableItem.updateState === OperationStatus.Completed) {
                status = Statuses.Success;
            } else if (tableItem.updateState === OperationStatus.Failed) {
                status = Statuses.Failed;
            } else if (tableItem.updateState === OperationStatus.InProgress) {
                status = Statuses.Running;
            }
        }

        const content = status && <Status {...status} size={StatusSize.m} />;
        return this._renderCellContent(columnIndex, tableColumn, content);
    };

    private _renderCellContent = (columnIndex: number, tableColumn: ITableColumn<BrokenLinkItem>, content: JSX.Element | undefined) => {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={"col-" + columnIndex}
                contentClassName="fontSizeM scroll-hidden"
            >
                {content}
            </SimpleTableCell>
        );
    };

    private _onSizeArtifactColumn = (event: MouseEvent, index: number, width: number): void => {
        (this._artifact_column.width as ObservableValue<number>).value = width;
    };

    private _onSizeLinkColumn = (event: MouseEvent, index: number, width: number): void => {
        (this._link_column.width as ObservableValue<number>).value = width;
    };

    private _onSizeStatusColumn = (event: MouseEvent, index: number, width: number): void => {
        (this._status_column.width as ObservableValue<number>).value = width;
    };

    private _getTableColumns(showStatusColumn?: boolean): ITableColumn<BrokenLinkItem>[] {
        const columns: ITableColumn<BrokenLinkItem>[] = [this._artifact_column, this._link_column];
        if (showStatusColumn) {
            columns.push(this._status_column);
        }

        return columns;
    }
}
